import React, { useState } from 'react';
import { AnalysisResult } from '../types';

interface TrainerInsightsProps {
  result: AnalysisResult;
}

export const TrainerInsights: React.FC<TrainerInsightsProps> = ({ result }) => {
  const [activeTab, setActiveTab] = useState<'calculations' | 'interventions'>('calculations');
  const { injuryPrevention } = result;
  
  if (!injuryPrevention) return null;

  const getOverallRisk = () => {
    const risks = [
      injuryPrevention.powerAsymmetry,
      100 - injuryPrevention.fatigueThreshold,
      injuryPrevention.overreachingRisk,
      injuryPrevention.techniqueBreakdown,
      100 - injuryPrevention.loadTolerance
    ];
    return risks.reduce((a, b) => a + b, 0) / risks.length;
  };

  const overallRisk = getOverallRisk();

  return (
    <div className="bg-theme-surface-1 border border-theme-border rounded-2xl p-6 shadow-card">
      <h3 className="text-2xl font-semibold tracking-tight text-theme-text mb-6">Trainer's Technical Guide</h3>
      
      {/* Tab Navigation */}
      <div className="flex space-x-4 mb-6 border-b border-theme-border">
        <button
          onClick={() => setActiveTab('calculations')}
          className={`pb-2 px-1 font-medium transition-colors ${
            activeTab === 'calculations'
              ? 'text-theme-text border-b-2 border-theme-accent'
              : 'text-theme-muted hover:text-theme-text'
          }`}
        >
          Calculation Methods
        </button>
        <button
          onClick={() => setActiveTab('interventions')}
          className={`pb-2 px-1 font-medium transition-colors ${
            activeTab === 'interventions'
              ? 'text-theme-text border-b-2 border-theme-accent'
              : 'text-theme-muted hover:text-theme-text'
          }`}
        >
          Training Interventions
        </button>
      </div>

      {activeTab === 'calculations' && (
        <div className="space-y-6">
          <div className="p-4 bg-theme-surface-2 rounded-lg">
            <h4 className="font-semibold text-theme-text mb-3">Mathematical Foundations</h4>
            <div className="space-y-4 text-sm">
              <div>
                <h5 className="font-medium text-theme-text mb-2">1. Power Asymmetry ({injuryPrevention.powerAsymmetry.toFixed(1)}%)</h5>
                <div className="bg-theme-surface-1 p-3 rounded font-mono text-xs">
                  <p>CV = (Standard Deviation / Mean) × 100</p>
                  <p>Asymmetry = CV × 2 (amplification factor)</p>
                  <p className="mt-2 text-theme-muted">Where: CV = Coefficient of Variation of power outputs</p>
                </div>
                <p className="text-theme-muted text-xs mt-2">
                  <strong>Rationale:</strong> CV &gt;7.5% indicates potential bilateral imbalances. 
                  Research shows &gt;15% asymmetry increases injury risk by 2.6x.
                </p>
              </div>

              <div>
                <h5 className="font-medium text-theme-text mb-2">2. Fatigue Threshold ({injuryPrevention.fatigueThreshold.toFixed(1)}%)</h5>
                <div className="bg-theme-surface-1 p-3 rounded font-mono text-xs">
                  <p>Power Decline = ((P₁ - Pₙ) / P₁) × 100</p>
                  <p>Threshold = 100 - (Power Decline × 2)</p>
                  <p className="mt-2 text-theme-muted">Where: P₁ = first trial, Pₙ = last trial</p>
                </div>
                <p className="text-theme-muted text-xs mt-2">
                  <strong>Rationale:</strong> &gt;25% power decline indicates neuromuscular fatigue. 
                  Multiplier accounts for non-linear fatigue accumulation.
                </p>
              </div>

              <div>
                <h5 className="font-medium text-theme-text mb-2">3. Overreaching Risk ({injuryPrevention.overreachingRisk.toFixed(1)}%)</h5>
                <div className="bg-theme-surface-1 p-3 rounded font-mono text-xs">
                  <p>Expected Power = Force × Velocity (from F-V line)</p>
                  <p>Deviation = |Actual - Expected| / Expected × 100</p>
                  <p>Risk = Average Deviation × 3</p>
                </div>
                <p className="text-theme-muted text-xs mt-2">
                  <strong>Rationale:</strong> Deviations from F-V relationship indicate altered neuromuscular function. 
                  &gt;15% deviation suggests overreaching state.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 bg-theme-surface-2 rounded-lg">
            <h4 className="font-semibold text-theme-text mb-3">Risk Thresholds & Evidence</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div className="p-3 bg-green-950 border border-green-800 rounded">
                <h5 className="text-green-400 font-medium mb-2">Low Risk (&lt;25%)</h5>
                <ul className="text-green-300 space-y-1">
                  <li>• Continue current training</li>
                  <li>• Normal progression rates</li>
                  <li>• Standard recovery protocols</li>
                </ul>
              </div>
              <div className="p-3 bg-yellow-950 border border-yellow-800 rounded">
                <h5 className="text-yellow-400 font-medium mb-2">Moderate Risk (25-50%)</h5>
                <ul className="text-yellow-300 space-y-1">
                  <li>• Monitor closely</li>
                  <li>• Slower progression</li>
                  <li>• Enhanced recovery</li>
                </ul>
              </div>
              <div className="p-3 bg-red-950 border border-red-800 rounded">
                <h5 className="text-red-400 font-medium mb-2">High Risk (&gt;50%)</h5>
                <ul className="text-red-300 space-y-1">
                  <li>• Immediate intervention</li>
                  <li>• Load reduction</li>
                  <li>• Active recovery focus</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'interventions' && (
        <div className="space-y-6">
          <div className="p-4 bg-theme-surface-2 rounded-lg">
            <h4 className="font-semibold text-theme-text mb-3">
              Current Risk Level: 
              <span className={`ml-2 px-3 py-1 rounded text-sm ${
                overallRisk < 25 ? 'bg-green-950 text-green-400' :
                overallRisk < 50 ? 'bg-yellow-950 text-yellow-400' :
                'bg-red-950 text-red-400'
              }`}>
                {overallRisk.toFixed(1)}% ({overallRisk < 25 ? 'Low' : overallRisk < 50 ? 'Moderate' : 'High'})
              </span>
            </h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-theme-text">Immediate Actions (This Week)</h4>
              
              {injuryPrevention.powerAsymmetry > 30 && (
                <div className="p-3 bg-red-950 border border-red-800 rounded">
                  <h5 className="text-red-400 font-medium mb-2">Address Power Asymmetry</h5>
                  <ul className="text-red-300 text-sm space-y-1">
                    <li>• Single-leg RDLs: 3×8 each leg</li>
                    <li>• Bulgarian split squats: 3×6 each</li>
                    <li>• Unilateral farmer's carries: 3×20m</li>
                    <li>• Movement screen for restrictions</li>
                  </ul>
                </div>
              )}

              {injuryPrevention.fatigueThreshold < 50 && (
                <div className="p-3 bg-red-950 border border-red-800 rounded">
                  <h5 className="text-red-400 font-medium mb-2">Combat Fatigue</h5>
                  <ul className="text-red-300 text-sm space-y-1">
                    <li>• Reduce volume by 30-40%</li>
                    <li>• Add 48h between intense sessions</li>
                    <li>• Sleep audit: target 8+ hours</li>
                    <li>• Stress management protocols</li>
                  </ul>
                </div>
              )}

              {injuryPrevention.overreachingRisk > 50 && (
                <div className="p-3 bg-red-950 border border-red-800 rounded">
                  <h5 className="text-red-400 font-medium mb-2">Overreaching Protocol</h5>
                  <ul className="text-red-300 text-sm space-y-1">
                    <li>• Deload week: 50% normal volume</li>
                    <li>• Active recovery only</li>
                    <li>• HRV monitoring daily</li>
                    <li>• Reassess in 7-10 days</li>
                  </ul>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-theme-text">Long-term Strategies (2-4 weeks)</h4>
              
              <div className="p-3 bg-theme-surface-1 border border-theme-border rounded">
                <h5 className="text-theme-text font-medium mb-2">Periodization Adjustments</h5>
                <ul className="text-theme-muted text-sm space-y-1">
                  <li>• Implement 3:1 loading pattern</li>
                  <li>• Weekly load increases: 5-10% max</li>
                  <li>• Planned deload every 4th week</li>
                  <li>• Monitor weekly load:recovery ratio</li>
                </ul>
              </div>

              <div className="p-3 bg-theme-surface-1 border border-theme-border rounded">
                <h5 className="text-theme-text font-medium mb-2">Movement Quality Focus</h5>
                <ul className="text-theme-muted text-sm space-y-1">
                  <li>• Video analysis bi-weekly</li>
                  <li>• Tempo work: 3-1-X-1 cadence</li>
                  <li>• Motor control drills daily</li>
                  <li>• Mobility screening monthly</li>
                </ul>
              </div>

              <div className="p-3 bg-theme-surface-1 border border-theme-border rounded">
                <h5 className="text-theme-text font-medium mb-2">Recovery Enhancement</h5>
                <ul className="text-theme-muted text-sm space-y-1">
                  <li>• Sleep hygiene protocols</li>
                  <li>• Nutrition timing optimization</li>
                  <li>• Stress management techniques</li>
                  <li>• Regular soft tissue work</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="p-4 bg-blue-950 border border-blue-800 rounded-lg">
            <h4 className="text-blue-400 font-semibold mb-2">Monitoring Protocol</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <h5 className="text-blue-300 font-medium mb-1">Daily</h5>
                <ul className="text-blue-200 text-xs space-y-1">
                  <li>• Subjective wellness (1-10)</li>
                  <li>• Sleep quality/duration</li>
                  <li>• HRV (if available)</li>
                </ul>
              </div>
              <div>
                <h5 className="text-blue-300 font-medium mb-1">Weekly</h5>
                <ul className="text-blue-200 text-xs space-y-1">
                  <li>• Training load calculation</li>
                  <li>• Movement quality check</li>
                  <li>• Performance benchmarks</li>
                </ul>
              </div>
              <div>
                <h5 className="text-blue-300 font-medium mb-1">Monthly</h5>
                <ul className="text-blue-200 text-xs space-y-1">
                  <li>• Full F-V profile reassessment</li>
                  <li>• Injury risk recalculation</li>
                  <li>• Program adjustments</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};